import { Icon } from './Icon'
import { useHelp } from './HelpProvider'
import { usePopover } from './usePopover'
import { TUTORIALS } from '../lib/tutorials'

/**
 * Help, in the top bar beside the identity: this screen's help, every walkthrough, and the keys.
 *
 * It replaced a lone question mark whose walkthroughs were at the bottom of a per-screen dialog, under
 * the lead, the points and the caveat - so the only way to learn there *were* walkthroughs was to ask
 * about a screen and read to the end. Listing them here puts all four one click from every screen.
 *
 * **Buttons in a `div`, not links in a `<nav>`.** Every item opens something - a dialog, the card -
 * rather than going somewhere, and `layout.spec.ts` asserts set equality over the hrefs inside the two
 * navigation landmarks. A third landmark, or an anchor inside one, would change that set for a menu
 * that has no destinations in it (D-495).
 *
 * The dismissal rules are `usePopover`'s, shared with the account menu beside it.
 */
export function HelpMenu() {
  const { entry, resume, openHelp, openShortcuts, start, carryOn } = useHelp()
  const { open, setOpen, close, container, toggle, listId } = usePopover()

  const suggested = entry.tutorials?.[0] ?? null

  /** Every item closes the menu first, so the thing it opens is not drawn underneath it. */
  const then = (action: () => void) => () => {
    close()
    action()
  }

  return (
    <div className={`help-menu${open ? ' open' : ''}`} ref={container}>
      {/*
        The word is visible from 700px up and kept as the accessible name below it, where the bar is
        tight: the phone gets the glyph and the same name.
      */}
      <button
        ref={toggle}
        type="button"
        className="help-toggle"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((was) => !was)}
        data-testid="help-menu"
      >
        <Icon name="question" size={17} />
        <span className="help-toggle-label">Help</span>
        <Icon name="caret-down" size={12} className="help-caret" />
      </button>

      <div id={listId} className="help-menu-panel" hidden={!open} data-testid="help-menu-panel">
        <button type="button" className="help-menu-item" onClick={then(openHelp)} data-testid="help-open-screen">
          <Icon name="question" size={18} />
          <span className="help-menu-text">
            <span className="help-menu-title">Help for this screen</span>
            <span className="help-menu-meta">{entry.title}</span>
          </span>
          <kbd aria-hidden="true">?</kbd>
        </button>

        <div className="help-menu-group" role="group" aria-label="Walkthroughs">
          <span className="help-menu-eyebrow">Walkthroughs</span>

          {TUTORIALS.map((tutorial) => {
            const inProgress = resume?.tutorial.id === tutorial.id

            return (
              <div key={tutorial.id} className={inProgress ? 'help-menu-row in-progress' : 'help-menu-row'}>
                <button
                  type="button"
                  className="help-menu-item"
                  onClick={then(() => start(tutorial.id))}
                  data-testid={`help-tutorial-${tutorial.id}`}
                >
                  <Icon name={tutorial.icon} size={18} />
                  <span className="help-menu-text">
                    <span className="help-menu-title">{tutorial.title}</span>
                    <span className="help-menu-meta">
                      {tutorial.steps.length} steps
                      {suggested === tutorial.id && (
                        <span className="help-menu-suggested" data-testid="help-suggested">
                          {' '}
                          · suggested here
                        </span>
                      )}
                    </span>
                  </span>
                </button>

                {/*
                  The place somebody left, on the row of the walkthrough it belongs to. Start over is
                  the row's own button above, which begins at step one.
                */}
                {inProgress && resume && (
                  <button
                    type="button"
                    className="help-menu-resume"
                    onClick={then(carryOn)}
                    data-testid="help-resume"
                  >
                    <Icon name="arrow-right" size={14} />
                    Carry on · Step {resume.step} of {resume.tutorial.steps.length}
                  </button>
                )}
              </div>
            )
          })}
        </div>

        <button
          type="button"
          className="help-menu-item"
          onClick={then(openShortcuts)}
          data-testid="help-open-shortcuts"
        >
          <Icon name="keyboard" size={18} />
          <span className="help-menu-text">
            <span className="help-menu-title">Keyboard shortcuts</span>
          </span>
        </button>
      </div>
    </div>
  )
}
